/**
 * ActivityController - Top-level facade for the Activity Engine.
 *
 * Parses URL parameters, loads activity data, initializes the correct plugin,
 * and provides named transition methods. Generalizes ChallengeController from bumblebee.
 *
 * @see GitHub Issue #852
 */

import ActivityStateMachine from './ActivityStateMachine';
import ActivityPluginRegistry from './ActivityPluginRegistry';
import ActivityLoader from './ActivityLoader';

export default class ActivityController {
    constructor () {
        this._stateMachine = null;
        this._plugin = null;
        this._activityData = null;
        this._ui = null;
    }

    get stateMachine () {
        return this._stateMachine;
    }

    get activityData () {
        return this._activityData;
    }

    get plugin () {
        return this._plugin;
    }

    /**
     * Initialize from URL parameters.
     * Expected params: ?mode=activity&type=guided-creation&activity=make-the-cat-dance&path=courses/first-steps/...
     * @param {HTMLElement} container - The editor container element
     * @returns {Promise<boolean>}
     */
    static async fromURLParams (container) {
        const params = new URLSearchParams(window.location.search);
        const mode = params.get('mode');

        if (mode !== 'activity' && mode !== 'challenge') {
            return null;
        }

        const controller = new ActivityController();

        // Support both new activity mode and legacy challenge mode
        const activityType = mode === 'challenge' ? 'challenge' : (params.get('type') || 'guided-creation');
        const activityPath = params.get('path') || params.get('level');

        if (!activityPath) {
            console.error('[ActivityController] No activity path specified');
            return null;
        }

        const success = await controller.init(activityType, activityPath, container);
        return success ? controller : null;
    }

    /**
     * Initialize the controller with a specific activity.
     * @param {string} activityType - Plugin type
     * @param {string} activityPath - Path to activity JSON
     * @param {HTMLElement} container - Editor container
     * @returns {Promise<boolean>}
     */
    async init (activityType, activityPath, container) {
        // Load activity data
        const result = await ActivityLoader.loadActivity(activityPath);
        if (!result.valid) {
            console.error('[ActivityController] Activity validation failed:', result.errors);
            return false;
        }

        this._activityData = result.data;

        // Verify plugin exists for this type
        const plugin = ActivityPluginRegistry.get(activityType);
        if (!plugin) {
            console.error(`[ActivityController] No plugin registered for type: ${activityType}`);
            return false;
        }
        this._plugin = plugin;

        // Create state machine
        this._stateMachine = new ActivityStateMachine(activityType);

        // Initialize plugin
        await Promise.resolve(plugin.init(this._activityData, this._stateMachine, container));

        // Transition to READY
        this._stateMachine.transition('READY');

        return true;
    }

    // --- Named transition methods ---

    startActivity () {
        const type = this._stateMachine.activityType;
        switch (type) {
        case 'challenge':
            return this._stateMachine.transition('BUILDING');
        case 'guided-creation':
            return this._stateMachine.transition('GUIDED_STEP', {stepIndex: 0});
        default:
            return this._stateMachine.transition('ACTIVE');
        }
    }

    advanceStep () {
        if (this._stateMachine.activityType !== 'guided-creation') return false;
        const nextStep = this._stateMachine.stepIndex + 1;
        const totalSteps = this._activityData.scaffolding.steps.length;

        if (nextStep >= totalSteps) {
            return this._stateMachine.transition('FREE_PLAY');
        }
        return this._stateMachine.transition('GUIDED_STEP', {stepIndex: nextStep});
    }

    startFreePlay () {
        return this._stateMachine.transition('FREE_PLAY');
    }

    run () {
        if (this._stateMachine.activityType === 'challenge') {
            return this._stateMachine.transition('RUNNING');
        }
        return this._stateMachine.transition('EVALUATING');
    }

    evaluate () {
        if (this._stateMachine.activityType === 'challenge') {
            return this._stateMachine.transition('VALIDATING');
        }
        return this._stateMachine.transition('EVALUATING');
    }

    succeed () {
        return this._stateMachine.transition('SUCCESS');
    }

    needsWork () {
        const type = this._stateMachine.activityType;
        if (type === 'challenge') {
            return this._stateMachine.transition('FAILED');
        }
        return this._stateMachine.transition('NEEDS_WORK');
    }

    retry () {
        const type = this._stateMachine.activityType;
        switch (type) {
        case 'challenge':
            return this._stateMachine.transition('BUILDING');
        case 'guided-creation':
            return this._stateMachine.transition('GUIDED_STEP', {stepIndex: 0});
        default:
            return this._stateMachine.transition('ACTIVE');
        }
    }

    reset () {
        return this._stateMachine.reset();
    }

    destroy () {
        if (this._plugin && typeof this._plugin.destroy === 'function') {
            this._plugin.destroy();
        }
        if (this._stateMachine) {
            this._stateMachine.destroy();
        }
        this._plugin = null;
        this._stateMachine = null;
        this._activityData = null;
    }
}
